/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { HoverEffectWrapper } from '@coursera-web/cds-labs';

import { MOMENT_FORMATS, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Button, Tag, Typography2, breakpoints } from '@coursera/cds-core';
import { ClockOutlineIcon } from '@coursera/cds-icons';
import type { SectionName } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import { useIntl } from 'bundles/page/lib/useIntl';
import { useModuleSectionEventTrackingData } from 'bundles/premium-hub/hooks/useModuleSectionEventTrackingData';
import type { Degree_WebinarFragment as DegreeWebinar } from 'bundles/premium-hub/queries/__generated__/getDegreeWebinars';
import { eventSchemaDataFromWebinar } from 'bundles/premium-hub/utils/structuredDataUtils';
import GoogleMarkup from 'bundles/seo/components/GoogleMarkup';

import _t from 'i18n!nls/premium-hub';

type UpcomingWebinarCardProps = {
  webinar: DegreeWebinar;
  sectionName: SectionName;
};

const MS_PER_HOUR = 60 * 60 * 1000;
const COUNTDOWN_WINDOW_HOURS = 24;

const styles = {
  card: css`
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: var(--cds-spacing-200);
    box-sizing: border-box;
    height: 100%;
    padding: var(--cds-spacing-300);
    border-radius: var(--cds-border-radius-200);
    background-color: var(--cds-color-blue-25);
  `,
  dateTag: css`
    background-color: var(--cds-color-white-0);
  `,
  startSoonTag: css`
    background: linear-gradient(90deg, var(--cds-color-yellow-500) -9.21%, var(--cds-color-pink-750) 100%);

    /* In dark mode, color to be white to keep contrast */
    /* stylelint-disable-next-line color-no-hex, function-no-unknown */
    color: light-dark(var(--cds-color-white-0), #fff);
  `,
  body: css`
    height: 100%;
    display: flex;
    align-items: self-start;
    justify-content: space-between;
    gap: var(--cds-spacing-200);

    ${breakpoints.down('xs')} {
      flex-direction: column;
    }
  `,
  details: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
    min-width: 0;
  `,
  dateTime: css`
    color: var(--cds-color-neutral-primary-weak);
  `,
  cta: css`
    &:not([aria-disabled='true']) {
      position: static !important;
    }

    ${breakpoints.down('xs')} {
      width: 100%;
    }

    &::before {
      content: '';
      position: absolute;
      inset: 0;
    }

    &::after {
      box-shadow: unset !important;
    }

    &:focus-visible {
      text-decoration: none;
      outline: 1px solid transparent;
      outline-offset: 2px;
      box-shadow:
        0 0 0 2px var(--cds-color-interactive-stroke-primary-focus-invert),
        0 0 0 3px var(--cds-color-interactive-primary);
    }
  `,
};

export const getHoursUntil = (date: Date, now: Date = new Date()) =>
  Math.max(1, Math.ceil((date.getTime() - now.getTime()) / MS_PER_HOUR));

export const UpcomingWebinarCard = ({ webinar, sectionName }: UpcomingWebinarCardProps) => {
  const intl = useIntl();
  const track = useTracker();
  const { index: sectionIndex } = useModuleSectionEventTrackingData();
  const { catalogDegree, timestamp, title, registerLink } = webinar;
  if (!timestamp?.seconds || !registerLink) return null;

  const webinarDate = new Date(parseInt(timestamp.seconds, 10) * 1000);
  const hoursUntil = getHoursUntil(webinarDate);
  const isStartingSoon = webinarDate > new Date() && hoursUntil <= COUNTDOWN_WINDOW_HOURS;
  const languageCode = catalogDegree?.languageCode;
  const structuredData = eventSchemaDataFromWebinar(webinar);
  const dateLabel = formatDateTimeDisplay(webinarDate, MOMENT_FORMATS.LONG_DATE_ONLY_DISPLAY);
  const timeLabel = formatDateTimeDisplay(webinarDate, MOMENT_FORMATS.TIME_ONLY_DISPLAY);
  const countdownLabel = intl.formatMessage(_t('In {hoursUntil, plural, =1 {1 hour} other {# hours}}'), {
    hoursUntil,
  });

  const onRegisterButtonClick = () => {
    track('click_button', {
      button: {
        name: 'degree_webinar_registration',
        linkType: 'external',
        linkURL: registerLink,
      },
      pageSection: { sectionName, index: sectionIndex },
    });
  };

  return (
    <>
      {structuredData && <GoogleMarkup graph={structuredData} />}
      <HoverEffectWrapper css={styles.card} data-testid="upcoming-webinar-card">
        <div>
          {isStartingSoon ? (
            <Tag color="ai" size="small" icon={<ClockOutlineIcon size="small" />} css={styles.startSoonTag}>
              {countdownLabel}
            </Tag>
          ) : (
            <Tag size="small" priority="secondary" css={styles.dateTag}>
              {dateLabel}
            </Tag>
          )}
        </div>
        <div css={styles.body}>
          <div css={styles.details}>
            <Typography2 component="h3" variant="subtitleMedium" lang={languageCode}>
              {title}
            </Typography2>
            <Typography2 component="p" variant="bodySecondary" css={styles.dateTime}>
              {_t('#{dateLabel} · #{timeLabel}', { dateLabel, timeLabel })}
            </Typography2>
          </div>

          <Button
            variant="primary"
            css={styles.cta}
            aria-label={_t('Register for #{title}', { title })}
            component="a"
            target="_blank"
            rel="noopener noreferrer"
            href={registerLink}
            hrefLang={languageCode}
            onClick={onRegisterButtonClick}
          >
            {_t('RSVP')}
          </Button>
        </div>
      </HoverEffectWrapper>
    </>
  );
};

export default React.memo(UpcomingWebinarCard);
